import type { ICarImageElement, ICarImages, IVehicle, IVehicleId } from 'data/schema';
import { userSession } from 'state/session';
import { gmsBrands, getImages } from 'utils/network/getCarImages';
import logger from 'utils/log';
import { identityMatchesAll, type IVehicleIdentity } from 'utils/vehicles';
import { truncateForLog, updateVehicle } from './vehicleRegistry';

/**
 * Why {@link fetchComparisonImagesForVehicle} stored nothing.
 *
 * - `vehicle-not-found`: the registry holds no entry for the id (never did, or it was removed while the request
 *   was in flight).
 * - `no-identity`: no request was made. The entry or the session lacks what the request needs: a brand and a
 *   market, plus a `vinOrMarketingCode` for a GMS brand (VW/VWN) or a `prString` for the others. Known
 *   limitation: a VW/VWN car announced through DCC's 3-arg `showCar` has `vinOrMarketingCode` '' and so gets no
 *   comparison images.
 * - `request-failed`: the transport rejected.
 * - `no-comparison-images`: the response held no `mode: 'comparison'` item. The entry is untouched, so comparison
 *   items it already holds stay.
 * - `vehicle-changed`: the entry's `prString` / `vinOrMarketingCode` changed while the request was in flight.
 * - `images-mismatch`: the entry holds `carImages` keyed to another `prString` than its own (a DCC push that
 *   landed before the announce, AVS-4399), or to an `okapiCode` other than its `vinOrMarketingCode` (GMS brands).
 *   Items are never attached under another car's key.
 */
export type TComparisonImagesSkipReason =
  | 'vehicle-not-found'
  | 'no-identity'
  | 'request-failed'
  | 'no-comparison-images'
  | 'vehicle-changed'
  | 'images-mismatch';

/**
 * Outcome of {@link fetchComparisonImagesForVehicle}. `comparisonCount` is the number of `mode: 'comparison'`
 * items the registry entry holds when the call returns (0 when the entry is gone).
 */
export type TComparisonImagesResult =
  | { stored: true; comparisonCount: number }
  | { stored: false; reason: TComparisonImagesSkipReason; comparisonCount: number };

/**
 * `items` without any comparison item, followed by `comparison`. The non-comparison items (the integrator's own
 * images and the demo shots) keep their order and are never touched; any previous comparison subset is swapped
 * for the new one, so merging twice never duplicates (AVS-4496). Pure.
 */
export function mergeComparisonItems(items: ICarImageElement[], comparison: ICarImageElement[]): ICarImageElement[] {
  return [...items.filter((item) => item.mode !== 'comparison'), ...comparison];
}

function countComparisonItems(vehicle: IVehicle | undefined): number {
  return vehicle?.carImages?.items.filter((item) => item.mode === 'comparison').length ?? 0;
}

function liveVehicle(vehicleId: IVehicleId): IVehicle | undefined {
  return userSession().get('vehicles')?.[vehicleId];
}

function skipped(vehicleId: IVehicleId, reason: TComparisonImagesSkipReason, detail: string): TComparisonImagesResult {
  logger.warn(
    `[ComparisonImages] Skipped comparison images for vehicle ${vehicleId} (${reason}): ${detail} (AVS-4496)`
  );
  return { stored: false, reason, comparisonCount: countComparisonItems(liveVehicle(vehicleId)) };
}

/** The identifiers the request is keyed on, taken from the entry itself. Empty values are left out. */
function identityOf(vehicle: IVehicle): IVehicleIdentity {
  return {
    ...(vehicle.prString ? { prString: vehicle.prString } : {}),
    ...(vehicle.vinOrMarketingCode ? { vinOrMarketingCode: vehicle.vinOrMarketingCode } : {}),
  };
}

/**
 * Whether the entry's `carImages` belong to another car: keyed to another `prString`, or carrying an `okapiCode`
 * (the GMS image key) other than the entry's `vinOrMarketingCode`. An absent `okapiCode` is no evidence either way.
 */
function holdsAnotherCarsImages(vehicle: IVehicle): boolean {
  const { carImages } = vehicle;
  if (!carImages) {
    return false;
  }
  return (
    carImages.prString !== vehicle.prString ||
    (!!carImages.okapiCode && carImages.okapiCode !== vehicle.vinOrMarketingCode)
  );
}

async function requestComparisonImages(request: Parameters<typeof getImages>[0]): Promise<ICarImages | undefined> {
  try {
    return await getImages(request);
  } catch (error) {
    logger.error('[ComparisonImages] Comparison-only image request failed (AVS-4496):', error);
    return undefined;
  }
}

/**
 * Fetches the comparison-only images of ONE registry entry and merges them next to the images it already holds
 * (AVS-4496). Used where the integrator controls the gallery (`imageOverrideMode === 'manual'`) and the
 * auto-updater therefore never fetches the 3D Exhibition's own comparison shots.
 *
 * The request is built from the entry the id names plus the session brand and market, never from the session root
 * or whichever vehicle is active when the response lands. On the response the live entry is read again and the
 * result is written in the same synchronous step, only when the entry still exists, still has the identity the
 * request was made for (`identityMatchesAll`, AVS-4400) and any `carImages` it already holds belong to it
 * (`prString`, and `okapiCode` when present). The write targets the id explicitly. Integrator items stay as they
 * are, in their order.
 *
 * Does not touch `carImagesStatus` and shows no spinner (re-entry is guarded in CarSharingControls).
 *
 * @param vehicleId - The registry id of the vehicle to fetch for
 * @returns Whether comparison items were stored, or why not, plus how many comparison items the entry now has.
 */
export async function fetchComparisonImagesForVehicle(vehicleId: IVehicleId): Promise<TComparisonImagesResult> {
  const vehicle = liveVehicle(vehicleId);
  if (!vehicle) {
    return skipped(vehicleId, 'vehicle-not-found', 'no such registry entry');
  }

  const brand = userSession().get('brand');
  const market = userSession().get('market');
  const hasKey = brand && gmsBrands.includes(brand) ? !!vehicle.vinOrMarketingCode : !!vehicle.prString;
  if (!brand || !market || !hasKey) {
    return skipped(vehicleId, 'no-identity', 'no brand, market or vehicle identifier to request with');
  }

  const requestedIdentity = identityOf(vehicle);
  const response = await requestComparisonImages({
    prString: vehicle.prString,
    brand,
    market,
    vinOrMarketingCode: vehicle.vinOrMarketingCode,
    mode: 'comparison',
  });
  if (!response) {
    return skipped(vehicleId, 'request-failed', 'the request rejected');
  }

  // An old lambda (deploy window) ignores `mode` and returns the demo items too.
  const comparisonItems = response.items.filter((item) => item.mode === 'comparison');
  if (!comparisonItems.length) {
    return skipped(vehicleId, 'no-comparison-images', 'the response held no comparison item');
  }

  // Read the live entry and write in this one synchronous step: nothing can interleave between the checks and
  // the write.
  const live = liveVehicle(vehicleId);
  if (!live) {
    return skipped(vehicleId, 'vehicle-not-found', 'the entry was removed while the request was in flight');
  }
  if (!identityMatchesAll(requestedIdentity, live)) {
    return skipped(
      vehicleId,
      'vehicle-changed',
      `requested for ${truncateForLog(requestedIdentity.prString)}, entry is now ${truncateForLog(live.prString)}`
    );
  }
  if (holdsAnotherCarsImages(live)) {
    return skipped(
      vehicleId,
      'images-mismatch',
      `entry holds images for ${truncateForLog(live.carImages?.prString ?? live.carImages?.okapiCode)}, not ${truncateForLog(live.prString ?? live.vinOrMarketingCode)}`
    );
  }

  const existing = live.carImages;
  const stored = updateVehicle(
    {
      carImages: {
        ...existing,
        prString: live.prString,
        items: mergeComparisonItems(existing?.items ?? [], comparisonItems),
      },
    },
    vehicleId
  );
  if (!stored) {
    return skipped(vehicleId, 'vehicle-not-found', 'the registry refused the write');
  }

  return { stored: true, comparisonCount: countComparisonItems(liveVehicle(vehicleId)) };
}
